import type {
  DagopvangDag,
  PensionKlantDagopvang,
  PensionKlantNotitie,
  PensionReservering,
  ReserveringStatus,
} from "../pensionKlantTypes";

/** Eén pensionboeking met al zijn dieren; de api levert een regel per dier. */
export interface Boeking {
  reserveringId: number;
  van: string;
  tot: string;
  nachten: number;
  status: ReserveringStatus;
  totaalBedrag: number | null;
  korting: number | null;
  betaald: number | null;
  openstaand: number | null;
  /** De factuur bij de boeking; `null` zolang er niet gefactureerd is. */
  factuur: { id: number; nummer: number | null; datum: string | null; status: string; openstaand: number | null } | null;
  dieren: { id: number | null; naam: string | null; kennel: string | null }[];
}

/** Voegt de regels van dezelfde reservering samen, in de volgorde van de api (nieuwste eerst). */
export function groepeerBoekingen(reserveringen: PensionReservering[]): Boeking[] {
  const perId = new Map<number, Boeking>();
  for (const r of reserveringen) {
    const bestaand = perId.get(r.reserveringId);
    const dier = { id: r.dierId, naam: r.dierNaam, kennel: r.kennel };
    if (bestaand) {
      bestaand.dieren.push(dier);
    } else {
      perId.set(r.reserveringId, {
        reserveringId: r.reserveringId,
        van: r.van,
        tot: r.tot,
        nachten: r.nachten,
        status: r.status,
        totaalBedrag: r.totaalBedrag,
        korting: r.korting ?? null,
        betaald: r.betaald ?? null,
        openstaand: r.openstaand ?? null,
        factuur:
          r.factuurId != null
            ? {
                id: r.factuurId,
                nummer: r.factuurNummer ?? null,
                datum: r.factuurDatum ?? null,
                status: r.factuurStatus ?? "OPEN",
                openstaand: r.factuurOpenstaand ?? null,
              }
            : null,
        dieren: [dier],
      });
    }
  }
  return [...perId.values()];
}

/** De boekingen per stand van zaken: wat nu loopt, wat komt (vroegste eerst), wat geweest is en wat verviel. */
export function verdeelBoekingen(boekingen: Boeking[]) {
  const van = (s: ReserveringStatus) => boekingen.filter((b) => b.status === s);
  return {
    nu: van("IN_PENSION"),
    komend: van("GEPLAND").sort((a, b) => a.van.localeCompare(b.van)),
    geweest: van("AFGEROND"),
    geannuleerd: van("GEANNULEERD"),
  };
}

/**
 * Welke boeking het tabblad Pension opent zonder keuze: wat nu loopt, anders wat het eerst komt, anders
 * het laatste verblijf dat doorging. Geannuleerd alleen als er niets anders is.
 */
export function standaardBoeking(boekingen: Boeking[]): Boeking | null {
  const nu = boekingen.find((b) => b.status === "IN_PENSION");
  if (nu) return nu;
  const eerstvolgend = boekingen.filter((b) => b.status === "GEPLAND").sort((a, b) => a.van.localeCompare(b.van))[0];
  if (eerstvolgend) return eerstvolgend;
  const laatste = (lijst: Boeking[]) => [...lijst].sort((a, b) => b.van.localeCompare(a.van))[0] ?? null;
  return laatste(boekingen.filter((b) => b.status !== "GEANNULEERD")) ?? laatste(boekingen);
}

/** "09:00" uit "09:00:00". */
export function tijd(waarde: string | null | undefined): string | null {
  return waarde ? waarde.slice(0, 5) : null;
}

/** Eén regel op het tabblad Reserveringen: een pensionboeking of een opvangdag. */
export type TijdlijnItem =
  | { soort: "pension"; datum: string; sleutel: string; boeking: Boeking }
  | { soort: "dagopvang"; datum: string; sleutel: string; dag: DagopvangDag };

/**
 * Pension en dagopvang door elkaar, zoals een klant zijn afspraken ziet. Wat nog komt of nu loopt staat
 * bovenaan, vroegste eerst; wat geweest is daaronder, meest recente eerst. Geannuleerd blijft erbuiten.
 */
export function tijdlijn(boekingen: Boeking[], dagopvang: PensionKlantDagopvang | undefined, vandaag: string) {
  const items: TijdlijnItem[] = [
    ...boekingen
      .filter((b) => b.status !== "GEANNULEERD")
      .map((b): TijdlijnItem => ({ soort: "pension", datum: b.van, sleutel: `p${b.reserveringId}`, boeking: b })),
    ...[...(dagopvang?.komend ?? []), ...(dagopvang?.geweest ?? [])]
      .filter((d) => d.status !== "GEANNULEERD" && d.datum)
      .map((d): TijdlijnItem => ({ soort: "dagopvang", datum: d.datum!, sleutel: `d${d.id}`, dag: d })),
  ];
  const isKomend = (i: TijdlijnItem) =>
    i.soort === "pension" ? i.boeking.status !== "AFGEROND" : i.datum >= vandaag;
  return {
    komend: items.filter(isKomend).sort((a, b) => a.datum.localeCompare(b.datum)),
    geweest: items.filter((i) => !isKomend(i)).sort((a, b) => b.datum.localeCompare(a.datum)),
  };
}

/**
 * Het oude pakket gebruikte {@code klant_notitie} als logboek: het schreef er elke wijziging, elke
 * afdruk en elke verstuurde mail in weg ("Aangemaakt", "Gewijzigde gegevens: …", "De 'Medicijn kaart'
 * is succesvol geprint.", "Pensionreservering R123 verwijderd."). In de werkdatabase is dat elke regel
 * van de ruim 600.000. Dit herkent die regels, zodat de notities die mensen schrijven niet tussen het
 * logboek verdwijnen.
 */
const AUTOMATISCH =
  /^(aangemaakt\b|gewijzigde gegevens|de (email )?'[^']*' is succesvol|\S*reservering \S+ (verwijderd|gewijzigd|aangemaakt))/i;

export function isAutomatisch(n: Pick<PensionKlantNotitie, "notitie">): boolean {
  return AUTOMATISCH.test(n.notitie.trim());
}
